/**
 * Design B client reels: film strip plus a story viewer.
 *
 * Strip: native scroll-snap (free touch and trackpad inertia), a mouse
 * drag-to-scroll for desktop, arrows, and a progress hairline.
 * Viewer: a modal <dialog>. Opening it is the user gesture that permits
 * sound. One clip at a time; when a clip ends the next one starts, after the
 * last one the viewer closes. Tap left or right to move, swipe down or Esc
 * to close.
 */
import { scene, revealAll } from '@theme/clinic-motion';
import { lockScroll, unlockScroll } from '@theme/utilities';

class ClinicStories extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.cards = [...this.querySelectorAll('[data-story]')];
    this.viewer = this.querySelector('[data-viewer]');
    this.video = this.querySelector('[data-viewer-video]');
    this.bars = this.querySelector('[data-bars]');
    this.handleEl = this.querySelector('[data-viewer-handle]');
    this.soundBtn = this.querySelector('[data-sound]');
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.bindStrip(signal);
    this.bindViewer(signal);

    this.dispose = scene(this, ({ scroller }) => revealAll(this, { scroller }));
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.dispose?.();
    this.close();
  }

  /* ---------------- Strip ---------------- */

  bindStrip(signal) {
    const track = this.track;
    const progress = this.querySelector('[data-progress]');
    const step = () => (this.cards[0]?.getBoundingClientRect().width || 280) + 18;

    this.querySelector('[data-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }), { signal });

    const syncProgress = () => {
      const max = track.scrollWidth - track.clientWidth;
      const visible = track.clientWidth / track.scrollWidth;
      const ratio = max > 0 ? track.scrollLeft / max : 0;
      if (progress) progress.style.transform = `translateX(${ratio * (1 - visible) * 100}%) scaleX(${visible})`;
    };
    track.addEventListener('scroll', syncProgress, { passive: true, signal });
    new ResizeObserver(syncProgress).observe(track);
    syncProgress();

    // Mouse drag to scroll; touch and trackpads already scroll natively.
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        down = true;
        moved = false;
        startX = event.clientX;
        startLeft = track.scrollLeft;
      },
      { signal }
    );
    window.addEventListener(
      'pointermove',
      (event) => {
        if (!down) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          track.setAttribute('data-dragging', '');
        }
        if (moved) track.scrollLeft = startLeft - dx;
      },
      { signal }
    );
    window.addEventListener(
      'pointerup',
      () => {
        if (!down) return;
        down = false;
        track.removeAttribute('data-dragging');
      },
      { signal }
    );
    // A drag must not also count as a click on the card under the pointer.
    track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      { capture: true, signal }
    );

    this.cards.forEach((card) =>
      card.addEventListener('click', () => this.open(Number(card.dataset.index) || 0, card), { signal })
    );
  }

  /* ---------------- Viewer ---------------- */

  bindViewer(signal) {
    if (!this.viewer || !this.video) return;

    this.bars.innerHTML = this.cards.map(() => '<span><i></i></span>').join('');
    this.barFills = [...this.bars.querySelectorAll('i')];

    this.querySelector('[data-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-zone-prev]')?.addEventListener('click', () => this.go(this.index - 1), { signal });
    this.querySelector('[data-zone-next]')?.addEventListener('click', () => this.go(this.index + 1), { signal });
    this.soundBtn?.addEventListener(
      'click',
      () => {
        this.video.muted = !this.video.muted;
        this.syncSound();
      },
      { signal }
    );

    this.viewer.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );
    this.viewer.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      },
      { signal }
    );
    // Click on the dark surround closes, like any lightbox.
    this.viewer.addEventListener('click', (event) => event.target === this.viewer && this.close(), { signal });

    this.video.addEventListener('ended', () => this.go(this.index + 1), { signal });
    this.video.addEventListener(
      'timeupdate',
      () => {
        const fill = this.barFills[this.index];
        if (fill && this.video.duration) fill.style.transform = `scaleX(${this.video.currentTime / this.video.duration})`;
      },
      { signal }
    );

    // Swipe down to close.
    let startY = null;
    this.viewer.addEventListener('touchstart', (event) => (startY = event.touches[0]?.clientY ?? null), { passive: true, signal });
    this.viewer.addEventListener(
      'touchend',
      (event) => {
        if (startY === null) return;
        const dy = (event.changedTouches[0]?.clientY ?? startY) - startY;
        startY = null;
        if (dy > 90) this.close();
      },
      { signal }
    );
  }

  open(index, returnFocus) {
    if (!this.viewer) return;
    this.returnFocus = returnFocus;
    if (!this.viewer.open) {
      this.viewer.showModal();
      lockScroll(this.viewer);
      this.video.muted = false;
      this.viewer.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
    }
    this.go(index);
  }

  go(index) {
    if (index < 0) index = 0;
    if (index >= this.cards.length) {
      this.close();
      return;
    }
    this.index = index;
    const card = this.cards[index];
    this.barFills.forEach((fill, i) => (fill.style.transform = `scaleX(${i < index ? 1 : 0})`));
    this.handleEl.textContent = card.dataset.handle || '';
    this.video.poster = card.dataset.poster || '';
    this.video.src = card.dataset.src;
    this.video.currentTime = 0;
    this.video.play().catch(() => {
      // Autoplay with sound refused (rare after a tap): fall back to muted.
      this.video.muted = true;
      this.syncSound();
      this.video.play().catch(() => {});
    });
    this.syncSound();
  }

  syncSound() {
    if (!this.soundBtn) return;
    this.soundBtn.setAttribute('aria-pressed', String(this.video.muted));
    this.soundBtn.setAttribute('aria-label', this.video.muted ? 'Unmute' : 'Mute');
  }

  close() {
    if (!this.viewer?.open) return;
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.viewer.close();
    unlockScroll(this.viewer);
    this.returnFocus?.focus?.({ preventScroll: true });
  }
}

if (!customElements.get('clinic-stories')) customElements.define('clinic-stories', ClinicStories);
